import { DataTable, Dialog, FormLabel, Heading, Loader, Select as CommunitySelect, NoData } from "@/components";
import useBudget from "./useBudget";
import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Filter,
  LayoutGrid,
  List,
  Plus,
  Search,
} from "lucide-react";
import { BUDGET_VIEW_TYPES, budgetStatusOptions, statusColor } from "./consts";
import { CopyBudget } from "./CopyBudget";

const Budget = () => {
  const {
    breadcrumbData,
    onBackClick,
    onCardClick,
    onAdd,
    budgetCards,
    budgetTableData,
    columns,
    renderBudgetActions,
    budgetSummaryData,
    yearOptions,
    globalFilter,
    setGlobalFilter,
    selectedYear,
    setSelectedYear,
    selectedStatus,
    setSelectedStatus,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    viewType,
    setViewType,
    onClear,
    isBudgetDataLoading,
    canAddBudget,
    rowSelected,
    openDeleteDialog,
    setOpenDeleteDialog,
    isDeletePending,
    onDeleteConfirm,
    showCopyDialog,
    setShowCopyDialog,
    openApprovalDialog,
    setOpenApprovalDialog,
    isSubmitApprovalPending,
    onSubmitApproval,
    openReviseDialog,
    setOpenReviseDialog,
    onRevisedConfirm,
    isRevisedBudgetPending,
    isMasterDataRoute,
    communityList,
    isCommunityLoading,
    communityId,
    onCommunityChange,
    communityKey,
  } = useBudget();

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col xl:flex-row gap-4 xl:items-center xl:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading
            title="Budget Management"
            breadcrumbData={breadcrumbData}
            showBack={!isMasterDataRoute}
            onBackClick={onBackClick}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          {isMasterDataRoute && (
            <div className="relative w-full sm:w-72  sm:min-w-72 sm:max-w-72 shrink-0">
              <FormLabel>Select Community</FormLabel>
              <CommunitySelect
                key={communityKey}
                placeholder={""}
                searchPlaceholder={""}
                noDataPlaceholder={"Community No Found"}
                options={(communityList?.data || [])?.map((data: any) => ({
                  value: data?.communityId?.toString(),
                  label: data?.name,
                }))}
                value={communityId?.toString()}
                onChange={onCommunityChange}
                className="h-[36px] w-full"
                disabled={isCommunityLoading}
              />
            </div>
          )}
          <Input
            type="text"
            placeholder="Search by budget name, ID or year..."
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="w-full sm:w-80 bg-white h-[40px]"
            startIcon={<Search className="w-4 h-4" />}
          />
          {canAddBudget && (
            <Button onClick={onAdd}>
              <Plus className="w-4 h-4" />
              Add Budget
            </Button>
          )}
        </div>
      </div>

      {isBudgetDataLoading ? (
        <Loader />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-5">
            {budgetSummaryData.map((data: any) => {
              const Icon = data.icon;

              return (
                <Card
                  key={data.key}
                  className={cn(
                    "rounded-xl border border-gray-200 border-l-4 bg-white py-0 shadow-sm",
                    statusColor.card[data.key] || statusColor.card.default
                  )}
                >
                  <CardContent className="flex items-center gap-4 p-4">
                    <div
                      className={cn(
                        "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
                        statusColor.icon[data.key] || statusColor.icon.default
                      )}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{data.title}</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {data.value}
                      </p>
                      <p className="text-sm text-gray-500">{data.caption}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="rounded-xl border border-gray-200 bg-white py-0 shadow-sm mb-5">
            <CardContent className="p-4 flex flex-row items-center justify-between gap-5">
              <div className="flex flex-row items-center gap-3">
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-full sm:w-[180px] bg-white h-[40px]">
                    <CalendarDays className="w-4 h-4 text-gray-500" />
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((data: any) => (
                      <SelectItem key={data.value} value={data.value}>
                        {data.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedStatus}
                  onValueChange={setSelectedStatus}
                >
                  <SelectTrigger className="w-full sm:w-[180px] bg-white h-[40px]">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {budgetStatusOptions.map((data) => (
                      <SelectItem key={data.value} value={data.value}>
                        {data.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button variant="outline" type="button" onClick={onClear} className="h-[40px]">
                  Clear
                </Button>
              </div>
              <div className="inline-flex overflow-hidden rounded-lg border border-blue-100 bg-white">
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    "rounded-none shadow-none",
                    viewType == BUDGET_VIEW_TYPES.CARD &&
                      "bg-blue-50 text-primary"
                  )}
                  onClick={() => setViewType(BUDGET_VIEW_TYPES.CARD)}
                >
                  <LayoutGrid className="w-4 h-4" />
                  Card View
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    "rounded-none shadow-none",
                    viewType == BUDGET_VIEW_TYPES.LIST &&
                      "bg-blue-50 text-primary"
                  )}
                  onClick={() => setViewType(BUDGET_VIEW_TYPES.LIST)}
                >
                  <List className="w-4 h-4" />
                  List View
                </Button>
              </div>
            </CardContent>
          </Card>

          {viewType == BUDGET_VIEW_TYPES.LIST ? (
            <DataTable
              key={`${globalFilter}-${selectedYear}-${selectedStatus}-${totalRecords}-${pageSize}`}
              columns={columns}
              data={budgetTableData}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onRowClick={(row: any) => onCardClick(row.original.budgetId)}
              className="shadow-none"
            />
          ) : (
            <Card className="shadow-none">
              <CardContent>
                {totalRecords ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {budgetCards.map((data: any) => {
                      const Icon = data.icon;

                      return (
                        <Card
                          key={data?.budgetId}
                          className={cn(
                            "rounded-xl border border-gray-200 border-l-4 bg-white py-0 shadow-sm cursor-pointer transition-all hover:shadow-md",
                            statusColor.card[data.statusKey] ||
                              statusColor.card.default
                          )}
                          onClick={() => onCardClick(data.budgetId)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3">
                                <div
                                  className={cn(
                                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                                    statusColor.icon[data.statusKey] ||
                                      statusColor.icon.default
                                  )}
                                >
                                  <Icon className="w-5 h-5" />
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">
                                    Budget ID
                                  </p>
                                  <p className="text-base font-semibold text-gray-900">
                                    {data?.budgetId || "-"}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <span
                                  className={cn(
                                    "rounded-full border px-3 py-1 text-xs font-medium",
                                    statusColor.badge[data.statusKey] ||
                                      statusColor.badge.default
                                  )}
                                >
                                  {data.statusLabel}
                                </span>
                                {renderBudgetActions(data)}
                              </div>
                            </div>

                            <h3 className="mt-4 text-lg font-semibold text-gray-900">
                              {data?.budgetName || "-"}
                            </h3>

                            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500">
                                  Financial Year
                                </p>
                                <p className="font-medium text-gray-900">
                                  {data?.year || "-"}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">Version No.</p>
                                <p className="font-medium text-gray-900">
                                  {data?.versionNo ?? "-"}
                                </p>
                              </div>
                            </div>

                            <div className="my-4 h-px bg-gray-100" />

                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500">Total Budget</p>
                                <p
                                  className={cn(
                                    "text-base font-semibold",
                                    statusColor.value[data.statusKey] ||
                                      statusColor.value.default
                                  )}
                                >
                                  {data.totalBudget}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">Submitted On</p>
                                <p className="font-medium text-gray-900">
                                  {data.submittedOn}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <NoData />
                )}

                {totalRecords ? (
                  <div className="flex flex-wrap items-center justify-end p-4 gap-4 text-sm text-g-400 text-[15px]">
                    <div className="flex items-center gap-2">
                      <span>Rows per page:</span>
                      <select
                        className="rounded px-1 py-1"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPageNumber(0);
                        }}
                      >
                        {[5, 10, 20, 50].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <span>
                        {pageNumber * pageSize + 1} -{" "}
                        {Math.min(
                          (pageNumber + 1) * pageSize,
                          totalRecords
                        )}{" "}
                        of {totalRecords}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() =>
                          setPageNumber((prev: number) =>
                            Math.max(prev - 1, 0)
                          )
                        }
                        disabled={pageNumber == 0}
                        className="px-2 py-1 shadow-none !bg-transparent"
                      >
                        <ChevronLeft className="w-6 h-6 text-g-400" />
                      </Button>
                      <Button
                        onClick={() =>
                          setPageNumber((prev: number) =>
                            Math.min(prev + 1, Math.max(totalPages - 1, 0))
                          )
                        }
                        disabled={pageNumber + 1 >= totalPages}
                        className="px-2 py-1 shadow-none !bg-transparent"
                      >
                        <ChevronRight className="w-6 h-6 text-g-400" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <></>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {openDeleteDialog && (
        <Dialog
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          saveText="Delete"
          saveBtnClassName="bg-red-500 text-white hover:bg-red-500 hover:text-white"
          isLoading={isDeletePending}
          onSave={onDeleteConfirm}
        >
          <p className="text-sm">
            Are you sure you want to delete the Annual Budget named -{" "}
            <span className="font-bold text-primary">
              {rowSelected?.budgetName}
            </span>
            ? This action will remove all associated line items and cannot be
            undone.
          </p>
        </Dialog>
      )}

      {openApprovalDialog && (
        <Dialog
          open={openApprovalDialog}
          setOpen={setOpenApprovalDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          saveText="Yes"
          cancelText="No"
          isLoading={isSubmitApprovalPending}
          onSave={onSubmitApproval}
        >
          <p className="text-sm">
            You are about to submit the budget for approval. Once submitted, the
            budget will move to{" "}
            <b className="text-primary">'Pending for Approval'</b> status and
            cannot be edited until the approval process is completed. Do you
            want to proceed?
          </p>
        </Dialog>
      )}

      {openReviseDialog && (
        <Dialog
          open={openReviseDialog}
          setOpen={setOpenReviseDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Revise Budget Confirmation !"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          contentClassName="!lg:w-[1000px] !lg:max-w-[1000px]"
          saveText="Yes, Create Revision"
          isLoading={isRevisedBudgetPending}
          onSave={onRevisedConfirm}
        >
          <div className="text-[14px]">
            <div className="italic mb-5">
              <b>You are about to revise an approved budget.</b>
              <br />
              <br />
              <ul className="list-disc pl-4">
                <li>The current approved version will be archived.</li>
                <li>A new version of the budget will be created.</li>
                <li>Budget status will change to Draft.</li>
                <li>
                  Financial postings against this budget will be temporarily
                  blocked until the revised budget is approved again by the
                  Board of Members (BM)
                </li>
              </ul>
              <br />
              <b>Restrictions:</b>
              <br />
              <br />
              <ul className="list-disc pl-4">
                <li>
                  Budget line items with posted financial transactions cannot be
                  deleted.
                </li>
                <li>Existing GL lines can be edited.</li>
                <li>New GL lines can be added.</li>
              </ul>
            </div>

            <b>Do you want to continue?</b>
          </div>
        </Dialog>
      )}

      {showCopyDialog && (
        <CopyBudget
          setShowCopyDialog={setShowCopyDialog}
          showCopyDialog={showCopyDialog}
          copyVersionDetails={rowSelected}
        />
      )}
    </div>
  );
};

export default Budget;
